import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, WifiOff, Languages, ArrowRight } from 'lucide-react';
import { request } from '../api/client';
import { usePermission, useAuthStore } from '../store/auth';
import { AI_LANGUAGES } from '../lib/aiLanguages';
import { ChatShell, type ChatMessage } from './ChatShell';
import { LangSelect } from './ChatControls';

// The starter chips SEND these English phrases as the query (the backend matches data intents by
// en/hi/mr keywords), while DISPLAYING the localized `chrome.starters` label — so a chip works in every
// language. Order must line up with every `starters` array below.
const STARTER_QUERIES = ['List my farmers', 'My retailers', 'Recent orders', 'Total outstanding'];

interface Chrome { greeting: string; placeholder: string; offlineBanner: string; subtitle: string; starters: string[] }

/** UI-chrome micro-copy per language (data replies are localized server-side). Falls back to English. */
const CHROME: Record<string, Chrome> = {
  en: { greeting: 'Hi! Ask about crops, products, dosage — or your data.', placeholder: 'Ask about crops, products, or your data…', offlineBanner: 'AI advice is offline right now — data lookups still work.', subtitle: 'Ask about your data or farming advice', starters: ['List my farmers', 'My retailers', 'Recent orders', 'Total outstanding'] },
  hi: { greeting: 'नमस्ते! फसल, उत्पाद, खुराक — या अपने डेटा के बारे में पूछें।', placeholder: 'फसल, उत्पाद या अपने डेटा के बारे में पूछें…', offlineBanner: 'AI सलाह अभी ऑफ़लाइन है — डेटा खोज अब भी काम करती है।', subtitle: 'अपने डेटा या खेती सलाह के बारे में पूछें', starters: ['मेरे किसान', 'मेरे रिटेलर', 'हाल के ऑर्डर', 'कुल बकाया'] },
  mr: { greeting: 'नमस्कार! पीक, उत्पादने, मात्रा — किंवा तुमच्या डेटाबद्दल विचारा.', placeholder: 'पीक, उत्पादने किंवा तुमच्या डेटाबद्दल विचारा…', offlineBanner: 'AI सल्ला सध्या ऑफलाइन आहे — डेटा शोध अजूनही काम करतो.', subtitle: 'तुमच्या डेटाबद्दल किंवा शेती सल्ल्याबद्दल विचारा', starters: ['माझे शेतकरी', 'माझे विक्रेते', 'अलीकडील ऑर्डर', 'एकूण थकबाकी'] },
  bn: { greeting: "নমস্কার! ফসল, পণ্য, ডোজ — বা আপনার তথ্য সম্পর্কে জিজ্ঞাসা করুন।", placeholder: "ফসল, পণ্য, বা আপনার তথ্য সম্পর্কে জিজ্ঞাসা করুন…", offlineBanner: "AI পরামর্শ এখন অফলাইন — তথ্য খোঁজা এখনও কাজ করছে।", subtitle: "আপনার তথ্য বা কৃষি পরামর্শ সম্পর্কে জিজ্ঞাসা করুন", starters: ["আমার কৃষকদের তালিকা", "আমার খুচরা বিক্রেতা", "সাম্প্রতিক অর্ডার", "মোট বকেয়া"] },
  or: { greeting: "ନମସ୍କାର! ଫସଲ, ଉତ୍ପାଦ, ମାତ୍ରା — କିମ୍ବା ଆପଣଙ୍କ ତଥ୍ୟ ବିଷୟରେ ପଚାରନ୍ତୁ।", placeholder: "ଫସଲ, ଉତ୍ପାଦ, କିମ୍ବା ଆପଣଙ୍କ ତଥ୍ୟ ବିଷୟରେ ପଚାରନ୍ତୁ…", offlineBanner: "AI ପରାମର୍ଶ ବର୍ତ୍ତମାନ ଅଫଲାଇନ୍ ଅଛି — ତଥ୍ୟ ଖୋଜା ଏବେ ବି କାମ କରୁଛି।", subtitle: "ଆପଣଙ୍କ ତଥ୍ୟ କିମ୍ବା କୃଷି ପରାମର୍ଶ ବିଷୟରେ ପଚାରନ୍ତୁ", starters: ["ମୋ କୃଷକ ତାଲିକା", "ମୋ ରିଟେଲର୍", "ସାମ୍ପ୍ରତିକ ଅର୍ଡର", "ମୋଟ ବକେୟା"] },
  te: { greeting: "నమస్కారం! పంటలు, ఉత్పత్తులు, మోతాదు — లేదా మీ డేటా గురించి అడగండి.", placeholder: "పంటలు, ఉత్పత్తులు, లేదా మీ డేటా గురించి అడగండి…", offlineBanner: "AI సలహా ప్రస్తుతం ఆఫ్‌లైన్‌లో ఉంది — డేటా శోధనలు ఇంకా పనిచేస్తాయి.", subtitle: "మీ డేటా లేదా వ్యవసాయ సలహా గురించి అడగండి", starters: ["నా రైతుల జాబితా", "నా రిటైలర్లు", "ఇటీవలి ఆర్డర్లు", "మొత్తం బకాయి"] },
  ta: { greeting: "வணக்கம்! பயிர்கள், பொருட்கள், அளவு பற்றி — அல்லது உங்கள் தரவு பற்றிக் கேளுங்கள்.", placeholder: "பயிர்கள், பொருட்கள் அல்லது உங்கள் தரவு பற்றிக் கேளுங்கள்…", offlineBanner: "AI ஆலோசனை இப்போது ஆஃப்லைனில் உள்ளது — தரவுத் தேடல்கள் இன்னும் செயல்படும்.", subtitle: "உங்கள் தரவு அல்லது விவசாய ஆலோசனை பற்றிக் கேளுங்கள்", starters: ["என் விவசாயிகள் பட்டியல்", "என் சில்லறை விற்பனையாளர்கள்", "சமீபத்திய ஆர்டர்கள்", "மொத்த நிலுவை"] },
  gu: { greeting: "નમસ્તે! પાક, પ્રોડક્ટ, ડોઝ — અથવા તમારા ડેટા વિશે પૂછો.", placeholder: "પાક, પ્રોડક્ટ અથવા તમારા ડેટા વિશે પૂછો…", offlineBanner: "AI સલાહ અત્યારે ઑફલાઇન છે — ડેટા શોધ હજુ કામ કરે છે.", subtitle: "તમારા ડેટા અથવા ખેતી સલાહ વિશે પૂછો", starters: ["મારા ખેડૂતોની યાદી", "મારા રિટેલર્સ", "તાજેતરના ઓર્ડર", "કુલ બાકી"] },
  pa: { greeting: "ਸਤ ਸ੍ਰੀ ਅਕਾਲ! ਫ਼ਸਲਾਂ, ਉਤਪਾਦਾਂ, ਖ਼ੁਰਾਕ — ਜਾਂ ਆਪਣੇ ਡੇਟੇ ਬਾਰੇ ਪੁੱਛੋ।", placeholder: "ਫ਼ਸਲਾਂ, ਉਤਪਾਦਾਂ, ਜਾਂ ਆਪਣੇ ਡੇਟੇ ਬਾਰੇ ਪੁੱਛੋ…", offlineBanner: "AI ਸਲਾਹ ਇਸ ਵੇਲੇ ਆਫ਼ਲਾਈਨ ਹੈ — ਡੇਟਾ ਖੋਜ ਹਾਲੇ ਵੀ ਕੰਮ ਕਰਦੀ ਹੈ।", subtitle: "ਆਪਣੇ ਡੇਟੇ ਜਾਂ ਖੇਤੀ ਸਲਾਹ ਬਾਰੇ ਪੁੱਛੋ", starters: ["ਮੇਰੇ ਕਿਸਾਨਾਂ ਦੀ ਸੂਚੀ", "ਮੇਰੇ ਰਿਟੇਲਰ", "ਹਾਲੀਆ ਆਰਡਰ", "ਕੁੱਲ ਬਕਾਇਆ"] },
  kn: { greeting: "ನಮಸ್ಕಾರ! ಬೆಳೆಗಳು, ಉತ್ಪನ್ನಗಳು, ಡೋಸೇಜ್ — ಅಥವಾ ನಿಮ್ಮ ಡೇಟಾ ಬಗ್ಗೆ ಕೇಳಿ.", placeholder: "ಬೆಳೆಗಳು, ಉತ್ಪನ್ನಗಳು ಅಥವಾ ನಿಮ್ಮ ಡೇಟಾ ಬಗ್ಗೆ ಕೇಳಿ…", offlineBanner: "AI ಸಲಹೆ ಸದ್ಯಕ್ಕೆ ಆಫ್‌ಲೈನ್ ಆಗಿದೆ — ಡೇಟಾ ಹುಡುಕಾಟ ಇನ್ನೂ ಕೆಲಸ ಮಾಡುತ್ತದೆ.", subtitle: "ನಿಮ್ಮ ಡೇಟಾ ಅಥವಾ ಕೃಷಿ ಸಲಹೆ ಬಗ್ಗೆ ಕೇಳಿ", starters: ["ನನ್ನ ರೈತರ ಪಟ್ಟಿ", "ನನ್ನ ಚಿಲ್ಲರೆ ವ್ಯಾಪಾರಿಗಳು", "ಇತ್ತೀಚಿನ ಆರ್ಡರ್‌ಗಳು", "ಒಟ್ಟು ಬಾಕಿ"] },
};
const chrome = (l: string): Chrome => CHROME[l] ?? CHROME.en;

// "View full list" — label for the deep-link a truncated data reply shows so the user can jump to the
// real list page (search / filters / pagination / CSV export) instead of the chat's top-N snapshot.
const VIEW_ALL: Record<string, string> = {
  en: 'View full list', hi: 'पूरी सूची देखें', mr: 'संपूर्ण यादी पहा',
  bn: 'সম্পূর্ণ তালিকা দেখুন', or: 'ସମ୍ପୂର୍ଣ୍ଣ ତାଲିକା ଦେଖନ୍ତୁ', te: 'పూర్తి జాబితా చూడండి',
  ta: 'முழு பட்டியலைக் காண்க', gu: 'સંપૂર્ણ યાદી જુઓ', pa: 'ਪੂਰੀ ਸੂਚੀ ਵੇਖੋ', kn: 'ಪೂರ್ಣ ಪಟ್ಟಿ ನೋಡಿ',
};

/**
 * Internal "Krishi Mitra" assistant. Answers scoped DATA questions (list my farmers/retailers/orders/
 * outstanding — each permission + territory checked server-side) and general farming advice. Gated by
 * AI.CHATBOT; shows an offline banner when the LLM is down (data lookups still work).
 */
export function ChatbotWidget() {
  const canChat = usePermission('AI.CHATBOT');
  // Role-aware starter chips (mirror the backend suggestFollowups gate): the "farmers / retailers" book
  // chips are STAFF-only — a customer would just see themselves / nothing. Orders + outstanding stay
  // (self-scoped) for anyone who can view them. Order lines up with STARTER_QUERIES.
  const role = useAuthStore((s) => s.user?.role);
  const isCustomer = role === 'RETAILER' || role === 'FARMER';
  const canFarmers = usePermission('FARMER.VIEW');
  const canRetailers = usePermission('RETAILER.VIEW');
  const canOrders = usePermission('ORDER.VIEW');
  const canOutstanding = usePermission('OUTSTANDING.VIEW');
  const canInvoice = usePermission('INVOICE.VIEW');
  const starterAllowed = [
    !isCustomer && canFarmers,        // 0 · List my farmers
    !isCustomer && canRetailers,      // 1 · My retailers
    canOrders,                        // 2 · Recent orders
    canOutstanding || canInvoice,     // 3 · Total outstanding
  ];
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [llmOffline, setLlmOffline] = useState(false);
  const [lang, setLang] = useState<string>(() => localStorage.getItem('gb-ai-lang') || 'en');
  const taRef = useRef<HTMLTextAreaElement>(null);

  const changeLang = (v: string) => { setLang(v); try { localStorage.setItem('gb-ai-lang', v); } catch { /* ignore */ } };

  // Probe the LLM once when opened, to decide whether to warn that only data lookups will work.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    request<any>({ url: '/ai/health' })
      .then((h) => { if (alive) setLlmOffline(h?.configured && !h?.reachable); })
      .catch(() => {});
    return () => { alive = false; };
  }, [open]);

  const tc = useMemo(() => chrome(lang), [lang]);
  const navigate = useNavigate();

  if (!canChat) return null;

  // `display` lets a starter chip SEND its English canonical query (so the backend intent matcher fires)
  // while SHOWING the localized label the user actually clicked. Typed messages pass only `text`.
  const sendText = async (text: string, display?: string) => {
    const msg = text.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: (display ?? text).trim() }]);
    setLoading(true);
    try {
      const r = await request<any>({ url: '/ai/chat', method: 'POST', data: { message: msg, sessionId, lang } });
      if (r?.sessionId) setSessionId(r.sessionId);
      const to: string | undefined = r?.cta?.to;
      // Drop any backend follow-up chip this role shouldn't see (same rule as the starters) — belt & braces
      // so a customer never gets a "my farmers / my retailers" chip even before the server-side gate ships.
      const suggestions: string[] = (Array.isArray(r?.suggestions) ? r.suggestions : [])
        .filter((qs: string) => { const idx = STARTER_QUERIES.indexOf(qs); return idx < 0 || starterAllowed[idx]; });
      // Under the bubble: a primary deep-link to the full list page (browse / search / export), then
      // secondary "you can also ask" chips for the OTHER commands — so a reply is never a dead-end. The
      // chips send the English canonical query but show the localized label (same split as the starters).
      const extra = (to || suggestions.length) ? (
        <div className="mt-1.5 flex flex-col items-start gap-1.5">
          {to && (
            <button
              type="button"
              onClick={() => { setOpen(false); navigate(to); }}
              className="inline-flex items-center gap-1 rounded-lg border border-brand-300 bg-brand-50 px-2.5 py-1 type-label text-brand-700 transition hover:bg-brand-100 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300"
            >
              {VIEW_ALL[lang] ?? VIEW_ALL.en} <ArrowRight size={12} />
            </button>
          )}
          {suggestions.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((qs) => {
                const idx = STARTER_QUERIES.indexOf(qs);
                const label = idx >= 0 ? tc.starters[idx] : qs;
                return (
                  <button
                    key={qs}
                    type="button"
                    onClick={() => sendText(qs, label)}
                    className="rounded-full border border-line bg-surface px-2.5 py-1 type-caption text-muted transition hover:border-brand-300 hover:text-brand-700 dark:hover:text-brand-300"
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : undefined;
      setMessages((m) => [...m, { role: 'assistant', content: r?.reply || '…', extra }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'Sorry, the assistant is unavailable right now. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const composer = (
    <div className="space-y-1.5">
      <div className="flex items-center justify-end gap-1">
        <Languages size={14} className="text-subtle" />
        <LangSelect value={lang} onChange={changeLang} options={AI_LANGUAGES} ariaLabel="Reply language" className="w-26" />
      </div>
      <div className="flex items-end gap-1.5">
        <textarea
          ref={taRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(input); } }}
          placeholder={tc.placeholder}
          aria-label={tc.placeholder}
          rows={1}
          className="max-h-24 flex-1 resize-none rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-500"
        />
        <button
          type="button"
          aria-label="Send"
          disabled={loading || !input.trim()}
          onClick={() => sendText(input)}
          className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-600 text-white transition hover:bg-brand-700 disabled:opacity-40"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );

  return (
    <ChatShell
      title="Krishi Mitra · AI Assistant"
      subtitle={tc.subtitle}
      open={open}
      onToggle={setOpen}
      messages={messages}
      loading={loading}
      fabAriaLabel="Open AI assistant"
      banner={llmOffline ? (
        <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-2xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          <WifiOff size={14} className="shrink-0" /> {tc.offlineBanner}
        </div>
      ) : undefined}
      emptyHint={
        <div>
          <p>{tc.greeting}</p>
          <div className="mt-3 flex flex-wrap justify-center gap-1.5">
            {tc.starters.map((s, i) => (starterAllowed[i] ? (
              <button
                key={STARTER_QUERIES[i]}
                type="button"
                onClick={() => sendText(STARTER_QUERIES[i], s)}
                className="rounded-full border border-brand-300 bg-brand-50 px-2.5 py-1 type-caption text-brand-700 transition hover:bg-brand-100 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300"
              >
                {s}
              </button>
            ) : null))}
          </div>
        </div>
      }
      composer={composer}
    />
  );
}
